import PropTypes from 'prop-types'
import React, {useEffect, useMemo} from 'react'
import {Box} from '@chakra-ui/react'
import Arlink from './arlink'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {productUrlBuilder} from '../../utils/url'
import {isProductSet} from '../../utils/product-utils'
import {useProductSetPrice} from '../../hooks/useScapiCustomApi'
import {isServer} from '../../utils/utils'
import {useIntl} from 'react-intl'
import ProductDisplayPrice from '../../components/product-display-price'
import HybridLink from '../../components/hybrid-link'
import {HYBRID_PDP} from '../../constants'

/**
 * Display ProductInfos (name & price)
 *
 * @param {string} pid - productId
 * @param {string} variant - default: name & price
 *                         - onlyPrice: price
 *
 * @example
 *   <pwa-widget-productinfos pid="5968">
 *     $include(...)$
 *   </pwa-widget-productinfos>
 */
const WidgetProductinfos = ({pid, variant}) => {
    const {locale} = useIntl()
    const {error: productError, data: product} = useProduct(
        {parameters: {id: pid}},
        {enabled: !!pid}
    )
    useEffect(() => {
        if (productError) {
            console.error(`Failed to load product ${pid}`, productError)
        }
    }, [pid, productError])

    // load set prices
    // promotions do not appear when loading product set with getProduct, so we need to fetch it in any case
    const needsFetchingCustomerPrice =
        Boolean(pid) && Boolean(product) && isProductSet(product) && !variant?.includes('arLink')
    const {
        data: productSetPricesData,
        error: productSetPricesError,
        isInitialLoading: isProductSetPricesLoading
    } = useProductSetPrice(
        {
            parameters: {
                locale,
                c_pid: pid
            }
        },
        {
            // fetch only on client to include customer prices
            enabled: needsFetchingCustomerPrice && !isServer
        }
    )
    useEffect(() => {
        if (productSetPricesError) {
            console.error(`Failed to load product set prices for ${pid}`, productSetPricesError)
        }
    }, [pid, productSetPricesError])
    const isPriceLoading = needsFetchingCustomerPrice && isProductSetPricesLoading

    const productWithPrice = useMemo(() => {
        if (needsFetchingCustomerPrice) {
            return {
                ...product,
                ...productSetPricesData?.resp
            }
        }
        return product
    }, [needsFetchingCustomerPrice, product, productSetPricesData])

    if (!pid || productError || productSetPricesError) {
        return null
    }

    // prevent initial flash of undefined product
    if (!productWithPrice) {
        return null
    }

    switch (variant) {
        case 'arLink':
        case 'arLinkPrimaryButton':
        case 'arLinkSecondaryButton':
            return <Arlink product={productWithPrice} variant={variant} />
        case 'onlyPrice':
            return (
                <ProductDisplayPrice
                    product={productWithPrice}
                    loading={isPriceLoading}
                    variant="productTile"
                    data-testid="product-tile-price"
                />
            )
        default:
            return (
                <Box pr={4} color="stokkeCore.black">
                    <HybridLink
                        page={HYBRID_PDP}
                        to={productUrlBuilder(productWithPrice, locale)}
                        pr={1}
                        width="100%"
                        size="sm"
                        display="inline"
                        verticalAlign="middle"
                    >
                        {productWithPrice.name}
                    </HybridLink>
                    <Box as="span" display="inline-block" width="max-content">
                        <ProductDisplayPrice
                            product={productWithPrice}
                            loading={isPriceLoading}
                            variant="productTile"
                            data-testid="product-tile-price"
                            display="flex"
                            verticalAlign="middle"
                        />
                    </Box>
                </Box>
            )
    }
}

WidgetProductinfos.propTypes = {
    pid: PropTypes.string,
    variant: PropTypes.string
}

export default WidgetProductinfos
